import Table from "@components/vendor/dashboard/table";
import { TTableColumns } from "@components/vendor/dashboard/table/types";
import { ActionIcon } from "@mantine/core";
import RoutesInstance from "@routes/instances";
import { useMutation } from "@tanstack/react-query";
import Common from "@utilities/common";
import DateHelper from "@utilities/date";
import { FC } from "react";
import { BiEdit } from "react-icons/bi";
import { IoMdRemoveCircle } from "react-icons/io";
import {
    Link,
    useLocation,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import Filters from "./filters";
import classNames from "classnames";
import {
    MdOutlinePublishedWithChanges,
    MdOutlineUnpublished,
} from "react-icons/md";
import { useAuth } from "@contexts/auth";

interface Props {
    data: Array<IArticles["get"]>;
    pagination: Required<IReturnValue["pagination"]>;
    loading: boolean;
}

const ArticlesContainer: FC<Props> = ({ data, loading, pagination }) => {
    const { pathname } = useLocation();

    const [URLSearchParams] = useSearchParams();

    const Navigate = useNavigate();

    const { profile } = useAuth();

    const {
        mutate: changePublishStatus,
        isLoading: isChangePublishStatusLoading,
    } = useMutation(
        ["publishArticle"],
        (id: number) =>
            Common.api.articles.Publish({
                id: id,
            }),
        {
            onSuccess: () => {
                URLSearchParams.set("refetch", "true");

                Navigate(
                    {
                        pathname,
                        search: "?" + URLSearchParams.toString(),
                    },
                    {
                        replace: true,
                    },
                );
            },
        },
    );

    const handlePublishStatus = (id: number) => {
        changePublishStatus(id);
    };

    const { mutate, isLoading } = useMutation(
        ["removeArticle"],
        (id: number) =>
            Common.api.articles.Delete({
                id: id,
            }),
        {
            onSuccess: () => {
                URLSearchParams.set("refetch", "true");

                Navigate(
                    {
                        pathname,
                        search: "?" + URLSearchParams.toString(),
                    },
                    {
                        replace: true,
                    },
                );
            },
        },
    );

    const handleRemove = (id: number) => {
        mutate(id);
    };

    const columns: TTableColumns<IArticles["get"]> = [
        {
            id: "thumbnail",
            accessorKey: "تصویر",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.thumbnail ? (
                        <img
                            height={48}
                            src={data.row.original.thumbnail}
                            alt={data.row.original.title}
                        />
                    ) : (
                        <div className="w-12">-</div>
                    )}
                </div>
            ),
        },
        {
            id: "title",
            accessorKey: "عنوان",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.title}
                </div>
            ),
        },
        {
            id: "isPublish",
            accessorKey: "وضعیت",
            cell: (data) => (
                <div
                    className={classNames(
                        "rtl:font-numeric flex items-center gap-2",
                        data.row.original.isPublish
                            ? "text-green-500"
                            : "text-red-500",
                    )}>
                    {data.row.original.isPublish
                        ? "منتشر شده"
                        : "در انتظار انتشار"}

                    {profile.role !== "admin" && (
                        <ActionIcon
                            variant="subtle"
                            color={
                                data.row.original.isPublish ? "red" : "green"
                            }
                            radius={9999}
                            className="mx-1"
                            size="lg"
                            loading={isChangePublishStatusLoading}
                            disabled={isLoading || isChangePublishStatusLoading}
                            onClick={() =>
                                handlePublishStatus(data.row.original.id)
                            }>
                            {data.row.original.isPublish ? (
                                <MdOutlineUnpublished className="text-2xl" />
                            ) : (
                                <MdOutlinePublishedWithChanges className="text-2xl" />
                            )}
                        </ActionIcon>
                    )}
                </div>
            ),
        },
        {
            id: "createDateTime",
            accessorKey: "زمان ساخت",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {DateHelper({
                        datetime: data.row.original.createDateTime,
                        format: "DD MMMM YYYY - HH:mm",
                    })}
                </div>
            ),
        },
        {
            accessorKey: "عملیات",
            cell: (data) => (
                <div className="flex items-center flex-wrap">
                    <Link
                        to={RoutesInstance.editArticle.replace(
                            ":id",
                            data.row.original.id.toString(),
                        )}>
                        <ActionIcon
                            variant="subtle"
                            color="yellow"
                            radius={9999}
                            className="mx-1"
                            size="lg"
                            disabled={
                                isLoading || isChangePublishStatusLoading
                            }>
                            <BiEdit className="text-2xl" />
                        </ActionIcon>
                    </Link>

                    {profile.role !== "admin" && (
                        <ActionIcon
                            variant="subtle"
                            color="red"
                            radius={9999}
                            className="mx-1"
                            size="lg"
                            loading={isLoading}
                            disabled={isLoading || isChangePublishStatusLoading}
                            onClick={() => handleRemove(data.row.original.id)}>
                            <IoMdRemoveCircle className="text-2xl" />
                        </ActionIcon>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <Filters />

            <Table<IArticles["get"]>
                columns={columns}
                data={data}
                loading={loading}
                options={{
                    rowNumber: true,
                }}
                pagination={{
                    activePage: pagination?.page || 0,
                    isStartFromZero: false,
                    lastPage: pagination
                        ? Math.ceil(pagination?.total / pagination?.limit)
                        : 1,
                    queryKey: "page",
                }}
            />
        </>
    );
};

export default ArticlesContainer;
